import SlDialog from '@shoelace-style/shoelace/dist/react/dialog/index.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/themes/light.css';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlSelect from '@shoelace-style/shoelace/dist/react/select/index.js';
import SlOption from '@shoelace-style/shoelace/dist/react/option/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import SlCheckbox from '@shoelace-style/shoelace/dist/react/checkbox/index.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from './useApi';

import './EditSourceModal.css';
import SourcePreview from './SourcePreview';

const EXTRACTION_METHOD_OPTIONS = [
    { value: 'wise_api', label: 'WISE' },
    { value: 'curl_cffi', label: 'RSS' },
    { value: 'ecfr_api', label: 'External API (ECFR)' }
];

export default function EditSourceModal({warningOpen, open, onClose, sourceData, onSubmit, retentionCategoryOptions = [], allSourcesData = [], isAddMode = false, isWarningMode = false, onConfirm, onCancel }) {
    const { authPut, authPost } = useApi();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [copyTooltipText, setCopyTooltipText] = useState('Copy URL');
    const retentionSelectRef = useRef(null);
    const [formData, setFormData] = useState({
        region: '',
        country: '',
        stateProvince: '',
        sourceName: '',
        url: '',
        linkDescription: '',
        category: '',
        retentionCategory: sourceData?.retentionCategory || '',
        extractionMethod: sourceData?.extractionMethod || ''
    });

    const [errors, setErrors] = useState({});
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const [isPreviewClicked, setIsPreviewClicked] = useState(false);
    const [isPreviewCompleted, setIsPreviewCompleted] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isSourceError, setIsSourceError] = useState(false);
    const [isPreviewError, setIsPreviewError] = useState(false);
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const previewRef = useRef(null);
    const sourceErrorMessageRef = useRef(null);
    // Initialize form data when modal opens or sourceData changes
    useEffect(() => {
        console.log("edit source modal open:", open, "isAddMode:", isAddMode, "sourceData:", sourceData);
        if (isAddMode) {
            // Reset form for add mode
            setFormData({
                region: '',
                country: '',
                stateProvince: '',
                sourceName: '',
                url: '',
                linkDescription: [''],
                category: [''],
                retentionCategory: 'Laws,_Regulations,_Guidelines',
                extractionMethod: 'wise_api'
            });
        } else if (sourceData) {
            // Convert "-" values to empty string for form display
            const cleanValue = (val) => (val === '-' || !val) ? '' : val;
            const selectedOption = retentionCategoryOptions.find(option => option.label === sourceData.retentionCategory);
            setFormData({
                srNo: sourceData.srNo,
                region: cleanValue(sourceData.region),
                country: cleanValue(sourceData.country),
                stateProvince: cleanValue(sourceData.stateProvince),
                sourceName: cleanValue(sourceData.sourceName),
                url: cleanValue(sourceData.url),
                linkDescription: cleanValue(sourceData.linkDescription),
                category: cleanValue(sourceData.category),
                retentionCategory: selectedOption ? selectedOption.value : '',
                extractionMethod: cleanValue(sourceData.extractionMethod),
                allDescriptions: sourceData.allDescriptions || [],
                allCategories: sourceData.allCategories || [],
                sourceIndex: sourceData.rowIndex || 0
            });
        }
    }, [sourceData, isAddMode, open]);

    // Programmatically set retention select value after render (handles comma-containing values)
    useEffect(() => {
        if (retentionSelectRef.current && formData.retentionCategory) {
            retentionSelectRef.current.value = formData.retentionCategory;
        }
    }, [formData.retentionCategory, open]);
    
    const dialogRef = useRef(null);
    const formContainerRef = useRef(null);

    // Scroll to preview title when preview loading completes
    useEffect(() => {
        if (isPreviewClicked && previewRef.current) {
            previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isPreviewClicked, isPreviewLoading]);

    //iswarning mode effect
    useEffect(() => {
        console.log("isWarningMode:", isWarningMode);
        if (isWarningMode) {
           const { allCategoriesArray, allDescriptionsArray } = generateCategoryAndDescription(sourceData.category, sourceData.linkDescription, sourceData.srNo, sourceData.id);
              console.log("allCategoriesArray:", allCategoriesArray, "allDescriptionsArray:", allDescriptionsArray);
           if (allCategoriesArray?.length > 1 || allDescriptionsArray?.length > 1) {
                setIsWarningVisible(true);
              }
              else{
                onConfirm(); // Call onConfirm directly without warning if no changes detected
              }
        }
    }, [isWarningMode]);

    //scroll to source error message when source error occurs
    useEffect(() => { 
        if (isSourceError && sourceErrorMessageRef.current) {
            sourceErrorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setIsSourceError(false); // Reset after scrolling
            setTimeout(() => {
                setErrors((prev) => {
                    const {sourceError, ...rest} = prev;
                    return rest;
                });
            }, 5000); // Clear error after 5 seconds
        }
    }, [isSourceError]);

    function handleInputChange(field, value) {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user edits
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }

    function handleLinkDescriptionChange(index, value) {
        setFormData(prev => {
            const updated = [...prev.linkDescription];
            updated[index] = value;
            return { ...prev, linkDescription: updated };
        });
    }

    function addLinkDescription() {
        setFormData(prev => ({
            ...prev,
            linkDescription: [...prev.linkDescription, '']
        }));
    }

    function removeLinkDescription(index) {
        setFormData(prev => ({
            ...prev,
            linkDescription: prev.linkDescription.filter((_, i) => i !== index)
        }));
    }

    function handleCategoryChange(index, value) {
        setFormData(prev => {
            const updated = [...prev.category];
            updated[index] = value;
            return { ...prev, category: updated };
        });
    }

    function addCategory() {
        setFormData(prev => ({
            ...prev,
            category: [...prev.category, '']
        }));
    }

    function removeCategory(index) {
        setFormData(prev => ({
            ...prev,
            category: prev.category.filter((_, i) => i !== index)
        }));
    }

    function isValidHttpUrl(url) {
        try{
            const parsedUrl = new URL(url);
            return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
        }
        catch{
            return false;
        }
    }

    function validateSource() {
        const errorMessage = 'This source already exist.';

        if(isAddMode){
            if(formData.url && allSourcesData.some(source => source.url === formData.url && (Array.isArray(formData.linkDescription) ? formData.linkDescription.some(desc => desc === source.linkDescription) : formData.linkDescription === source.linkDescription) && (Array.isArray(formData.category) ? formData.category.some(cat => cat === source.category) : formData.category === source.category))) {
            setErrors(prev => ({
                ...prev,
                sourceError: errorMessage
            }));
            setIsSourceError(true);
            return false;
        }
        }
        else{
            return true;
        }
        
        return true;
    }

    function validateForm() {
        const newErrors = {};
        
        if (!formData.region.trim()) newErrors.region = 'Region is required';
        if (!formData.sourceName.trim()) newErrors.sourceName = 'Source Name is required';
        if(Array.isArray(formData.linkDescription)){
            if (formData.linkDescription.some(desc => !desc?.trim())) newErrors.linkDescription = 'Link Description is required';
        }
        else{
            if (!formData.linkDescription.trim()) newErrors.linkDescription = 'Link Description is required';
        }
        if(Array.isArray(formData.category)){
            if (formData.category.some(cat => !cat?.trim())) newErrors.category = 'Category is required';
        }
        else{
            if (!formData.category.trim()) newErrors.category = 'Category is required';
        }
        if (!formData.url.trim()) newErrors.url = 'URL is required';
        else if (!isValidHttpUrl(formData.url.trim())) newErrors.url = 'Enter a valid URL';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function generateCategoryAndDescription(category, description, srNo, sourceId) {
        const allCategories = [];
        const allDescriptions = [];
        allSourcesData.forEach(source => {
            if (source.id === sourceId) {
                (source.srNo === srNo && category !== source.category) ? allCategories.push({ id: source.srNo, category: category }) : allCategories.push({ id: source.srNo, category: source.category });
                (source.srNo === srNo && description !== source.linkDescription)? allDescriptions.push({ id: source.srNo, description: description }) : allDescriptions.push({ id: source.srNo, description: source.linkDescription });
            }
        });
        const allDescriptionsArray = allDescriptions.map(item => item.description.trim());
        const allCategoriesArray = allCategories.map(item => item.category.trim());
        return { allCategoriesArray, allDescriptionsArray };
        
    }

    async function handleSubmit() {
        if (!validateForm()) return;
        if(!validateSource()) return;

        setIsSubmitting(true);

        let payload;
        
        if (isAddMode) {
            // Add mode: camelCase payload with isActive always true
            payload = {
                baseUrl: "",
                sourceName: "",
                defaultCategory: "",
                isActive: true,
                defaultRegion: formData.region,
                defaultCountry: formData.country,
                defaultState: "",
                sourceTitle: "",
                triggerImmediately: true
            };
            if (formData.stateProvince?.trim()) payload.defaultState = formData.stateProvince;
            if (formData.sourceName?.trim()) payload.sourceName = formData.sourceName;
            if (formData.url?.trim()) payload.baseUrl = formData.url;
            // Filter out empty link descriptions and pass as array
            const filteredDescriptions = formData.linkDescription.filter(desc => desc?.trim());
            if (filteredDescriptions.length > 0) payload.sourceTitle = filteredDescriptions;
            // Filter out empty categories and pass as array
            const filteredCategories = formData.category.filter(cat => cat?.trim());
            if (filteredCategories.length > 0) payload.defaultCategory = filteredCategories;
            if (formData.retentionCategory?.trim()) payload.policyType = formData.retentionCategory.replace(/_/g, ' ');
            if (formData.extractionMethod?.trim()) payload.extractionMethod = formData.extractionMethod;
            
        } else {
            // Edit mode: snake_case payload
            payload = {
                default_region: formData.region,
                default_country: formData.country
            };
            //create an array of all categories and all description which belongs to this source id
            const { allCategoriesArray, allDescriptionsArray } = generateCategoryAndDescription(formData.category, formData.linkDescription, formData.srNo, sourceData.id);

            if (formData.stateProvince?.trim()) payload.default_state = formData.stateProvince;
            if (formData.sourceName?.trim()) payload.source_name = formData.sourceName;
            if (formData.url?.trim()) payload.base_url = formData.url;
            if (allDescriptionsArray) payload.source_title = allDescriptionsArray;
            if (allCategoriesArray) payload.default_category = allCategoriesArray;
            if (formData.retentionCategory?.trim()) payload.policy_type = formData.retentionCategory.replace(/_/g, ' ');
            if (formData.extractionMethod?.trim()) payload.extraction_method = formData.extractionMethod;
        }

        try {
            let response;
            if (isAddMode) {
                response = await authPost(`${apiBaseUrl}/source`, payload);
            } else {
                response = await authPut(`${apiBaseUrl}/sources/${sourceData.id}`, payload);
            }
            if (response.ok) {
                const responseData = isAddMode ? await response.json() : null;
                onSubmit({ id: responseData?.id || sourceData?.id, lastRunStatus: responseData?.lastRunStatus, ...formData });
            } else {
                console.error(`Failed to ${isAddMode ? 'add' : 'update'} source:`, response.status);
            }
        } catch (error) {
            console.error(`Error ${isAddMode ? 'adding' : 'updating'} source:`, error);
        } finally {
            setIsSubmitting(false);
            setPreviewData(null);
            setIsPreviewClicked(false);
            setIsPreviewLoading(false);
            setIsPreviewCompleted(false);
        }
    }

    function handleClose() {
        setErrors({});
        onClose();
        setIsPreviewClicked(false);
        setIsPreviewLoading(false);
        setIsPreviewCompleted(false);
    }

    // Prevent modal from closing when dropdown closes
    function handleRequestClose(event) {
        if (event.detail.source === 'close-button' || event.detail.source === 'keyboard') {
            handleClose();
        } else {
            event.preventDefault();
        }
    }

    //handle preview click
    function handlePreviewClick() {
        if (!validateForm()) return;
        if(!validateSource()) return;

        setIsPreviewClicked(true);
        setIsPreviewLoading(true);
        // Scroll to bottom of the form/dialog first
        if (formContainerRef.current) {
            formContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
        fetchPreviewData();
    }

    function cancelWarning() {
        setIsWarningVisible(false);
        onCancel();
    }

    function confirmWarning() {
        setIsWarningVisible(false);
        onConfirm();
    }

    // fetch preview data
    async function fetchPreviewData(){
        const payload = { "keyword": "", "applyFilters": false, "sortOrder": "DESC", "page": 1, "pageSize": 10 }
        try{
            setIsPreviewError(false);
            const response = await authPost(`${apiBaseUrl}/records`, payload);
            const data = await response.json();
            console.log("Records fetched for preview:", data);

            const previewPayload = {
                sourceConfig: {
                    baseUrl: "",
                    sourceName: "",
                    defaultCategory: [],
                    isActive: true,
                    defaultRegion: formData.region,
                    defaultCountry: formData.country,
                    defaultState: "",
                    sourceTitle: [],
                    triggerImmediately: true,
                    extractionMethod: "",
                    retentionCategory: ""
                }
            };

            //fetching data from the form and adding to the preview payload
            if (formData.stateProvince?.trim()) previewPayload.sourceConfig.defaultState = formData.stateProvince;
            if (formData.sourceName?.trim()) previewPayload.sourceConfig.sourceName = formData.sourceName;
            if (formData.url?.trim()) previewPayload.sourceConfig.baseUrl = formData.url;
            // Filter out empty link descriptions and pass as array
            const filteredDescriptions = formData.linkDescription.filter(desc => desc?.trim());
            if (filteredDescriptions.length > 0) previewPayload.sourceConfig.sourceTitle = filteredDescriptions;
            // Filter out empty categories and pass as array
            const filteredCategories = formData.category.filter(cat => cat?.trim());
            if (filteredCategories.length > 0) previewPayload.sourceConfig.defaultCategory = filteredCategories;
            if (formData.retentionCategory?.trim()) previewPayload.sourceConfig.retentionCategory = formData.retentionCategory.replace(/_/g, ' ');
            if (formData.extractionMethod?.trim()) previewPayload.sourceConfig.extractionMethod = formData.extractionMethod;

            //preview api call
            const previewResponse = await authPost(`${apiBaseUrl}/preview`, previewPayload);
            const previewRecord = await previewResponse.json();
            setPreviewData(previewRecord.previewItems[0]);
            setIsPreviewCompleted(true);
            setIsPreviewLoading(false);
        }
        catch(error){
            console.error("Error fetching preview data:", error);
            setIsPreviewError(true);
        }
    }
    return (
        <>
            {isWarningVisible && (
                <SlDialog
                    className="cds-scrim-dark edit-source-modal"
                    label="Linked Sources Impact Warning"
                    open={isWarningVisible}
                    onSlRequestClose={() => cancelWarning()}
                >
                    <div className="warning-dialog-content">
                        <p>This source is linked to one or more sources. Any changes you make to the source configuration will also affect all linked sources that share this URL.</p>
                        
                    </div>
                    <div className="cds-dialog-footer-layout" slot="footer">
                            <SlButton size="medium" variant="primary" onClick={() => confirmWarning()}>OK</SlButton>
                            <SlButton size="medium" variant="default" onClick={() => cancelWarning()}>Cancel</SlButton>
                        </div>
                </SlDialog>
            )}
            <SlDialog
            ref={dialogRef}
            className="cds-scrim-dark edit-source-modal"
            label={isAddMode ? "Add Source" : "Edit Source"}
            open={open}
            onSlRequestClose={handleRequestClose}
        >
           <div className="edit-source-form" ref={formContainerRef}>
                {errors.sourceError && (
                    <div ref={sourceErrorMessageRef} className="form-row">
                        <div className="form-field full-width">
                            <div className="source-error-message">{errors.sourceError}</div>
                        </div>
                    </div>
                )}
                <div className="form-row">
                    <div className="form-field">
                        <label>Region <span className="required">*</span></label>
                        <SlInput
                            value={formData.region}
                            onSlInput={(e) => handleInputChange('region', e.target.value)}
                            size="medium"
                        />
                        {errors.region && <span className="error-text">{errors.region}</span>}
                    </div>
                    <div className="form-field">
                        <label>Country</label>
                        <SlInput
                            value={formData.country}
                            onSlInput={(e) => handleInputChange('country', e.target.value)}
                            size="medium"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>State/Province</label>
                        <SlInput
                            value={formData.stateProvince}
                            onSlInput={(e) => handleInputChange('stateProvince', e.target.value)}
                            size="medium"
                        />
                    </div>
                    <div className="form-field">
                        <label>Source name <span className="required">*</span></label>
                        <SlInput
                            value={formData.sourceName}
                            onSlInput={(e) => handleInputChange('sourceName', e.target.value)}
                            size="medium"
                        />
                        {errors.sourceName && <span className="error-text">{errors.sourceName}</span>}
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-field full-width">
                        <label>URL<span className="required">*</span></label>
                        {isAddMode ? (
                            <SlInput
                                value={formData.url}
                                onSlInput={(e) => handleInputChange('url', e.target.value)}
                                size="medium"
                            />
                        ) : (
                            <div className="url-display">
                                <span className="url-text">{formData.url}</span>
                                <SlTooltip content={copyTooltipText} hoist>
                                    <SlIcon
                                        onClick={() => {
                                            navigator.clipboard.writeText(formData.url)
                                                .then(() => setCopyTooltipText('URL copied'))
                                                .catch(err => console.error('Failed to copy URL:', err));
                                        }}
                                        onMouseEnter={() => setCopyTooltipText('Copy URL')}
                                        label="Copy URL"
                                        style={{ cursor: 'pointer', marginLeft: '8px' }}
                                        src="/Icons/common-file-double-2.svg"
                                        className="copy-url-icon"
                                    />
                                </SlTooltip>
                            </div>
                        )}
                        {errors.url && <span className="error-text">{errors.url}</span>}
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-field full-width">
                        <label>Link description <span className="required">*</span></label>
                        {isAddMode && Array.isArray(formData.linkDescription) ? (
                            <div className="link-description-list">
                                {formData.linkDescription.map((desc, index) => (
                                    <div key={index} className="input-with-button">
                                        <SlInput
                                            value={desc}
                                            onSlInput={(e) => handleLinkDescriptionChange(index, e.target.value)}
                                            size="medium"
                                        />
                                        {index === formData.linkDescription.length - 1 ? (
                                            <SlButton
                                                size="medium"
                                                variant="default"
                                                onClick={addLinkDescription}
                                                className="add-button"
                                            >
                                                +
                                            </SlButton>
                                        ) : (
                                            <SlButton
                                                size="medium"
                                                variant="default"
                                                onClick={() => removeLinkDescription(index)}
                                                className="remove-button"
                                            >
                                                −
                                            </SlButton>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <SlInput
                                value={formData.linkDescription}
                                onSlInput={(e) => handleInputChange('linkDescription', e.target.value)}
                                size="medium"
                            />
                        )}
                        {errors.linkDescription && <span className="error-text">{errors.linkDescription}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field full-width">
                        <label>Category <span className="required">*</span></label>
                        {isAddMode && Array.isArray(formData.category) ? (
                            <div className="link-description-list">
                                {formData.category.map((cat, index) => (
                                    <div key={index} className="input-with-button">
                                        <SlInput
                                            value={cat}
                                            onSlInput={(e) => handleCategoryChange(index, e.target.value)}
                                            size="medium"
                                        />
                                        {index === formData.category.length - 1 ? (
                                            <SlButton
                                                size="medium"
                                                variant="default"
                                                onClick={addCategory}
                                                className="add-button"
                                            >
                                                +
                                            </SlButton>
                                        ) : (
                                            <SlButton
                                                size="medium"
                                                variant="default"
                                                onClick={() => removeCategory(index)}
                                                className="remove-button"
                                            >
                                                −
                                            </SlButton>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <SlInput
                                value={formData.category}
                                onSlInput={(e) => handleInputChange('category', e.target.value)}
                                size="medium"
                            />
                        )}
                        {errors.category && <span className="error-text">{errors.category}</span>}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Retention category</label>
                        {retentionCategoryOptions.length > 0 && <SlSelect
                            ref={retentionSelectRef} 
                            value={formData.retentionCategory}
                            onSlChange={(e) => handleInputChange('retentionCategory', e.target.value)}
                            size="medium"
                            placement="bottom"
                            hoist
                            className="retention-category-select"
                        >
                            {retentionCategoryOptions.map((option, idx) => (
                                <SlOption key={idx} value={option.value}>{option.label}</SlOption>
                            ))}
                        </SlSelect>}
                        {errors.retentionCategory && <span className="error-text">{errors.retentionCategory}</span>}
                    </div>
                </div>

                {isAddMode ? <div className="form-row">
                    <div className="form-field">
                        <label>Extraction method</label>
                        <SlSelect
                            key={`extraction-${formData.extractionMethod}`}
                            value={formData.extractionMethod}
                            onSlChange={(e) => handleInputChange('extractionMethod', e.target.value)}
                            placeholder="Select extraction method"
                            size="medium"
                            placement="bottom"
                            hoist
                            className="retention-category-select"
                        >
                            {EXTRACTION_METHOD_OPTIONS.map(option => (
                                <SlOption key={option.value} value={option.value}>{option.label}</SlOption>
                            ))}
                        </SlSelect>
                    </div>
                </div> : '' }

            </div>
            <div ref={previewRef} style={{ marginTop: '20px' }}>
            {isAddMode && isPreviewClicked && <SourcePreview ref={previewRef} record={previewData} isPreviewLoading={isPreviewLoading} setIsPreviewClicked={setIsPreviewClicked} isPreviewError={isPreviewError} />}
            </div>   

            <div className="cds-dialog-footer-layout" slot="footer">
                <SlButton
                    size="medium"
                    variant="default"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </SlButton>
                {isAddMode ? (
                    isPreviewCompleted ? (
                        <SlButton
                            size="medium"
                            variant="primary"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Submit
                        </SlButton>
                    ) : (
                        <SlButton
                            size="medium"
                            variant="primary"
                            onClick={handlePreviewClick}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Preview
                        </SlButton>
                    )
                ) : (
                    <SlButton
                        size="medium"
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                    >
                        Submit
                    </SlButton>
                )}
            </div>
        </SlDialog>
        </>
        
)}


 